import React, { useEffect, useMemo, useState } from 'react';
import {
    ColumnOrder,
    EmployeesPageQuery,
    PaginatedEmployeesSortableFields,
    useEmployeesPageLazyQuery,
} from '../../../../../services';
import {
    Continued,
    CTableWrapper,
    DBlock,
    DeleteIcon,
    DIdAutocomplete,
    DPage,
    DPageBody,
    DPaginator,
    DTextField,
    EditIcon,
    SortedOrder,
    usePaginator,
} from '../../../../dum';
import {
    SDeleteEmployeeDialog,
    SUpsertEmployeeDialog,
} from '../../../../smart';
import { Box, IconButton, Typography } from '@mui/material';
import {
    COLUMN_LABELS,
    FIELD_LABELS,
    shortLabel,
    useNumber,
    useString,
} from '../../../../../helpers';

export function EmployeesPage() {
    const [query, { data, previousData, loading }] =
        useEmployeesPageLazyQuery();
    const [employeeId, setEmployeeId] = useState<null | number>(null);
    const [upsertOpen, setUpsertOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const [sortedIndex, setSortedIndex] = useState<number | null>(null);
    const [sortedOrder, setSortedOrder] = useState<SortedOrder>(null);

    const [branchId, setBranchId] = useNumber({
        KEY: EmployeesPage.name + '_BranchId',
        defaultValue: null,
    });
    const [orderProductionTypeId, setOrderProductionTypeId] = useNumber({
        KEY: EmployeesPage.name + '_OrderProductionTypeId',
        defaultValue: null,
    });

    const [filter, setFilter] = useString({
        KEY: EmployeesPage.name + '_Filter',
    });

    const { setSkip, skip, take } = usePaginator(
        {
            KEY: 'ProductsPage',
        },
        [filter, orderProductionTypeId, branchId],
    );

    useEffect(() => {
        void query({
            variables: {
                skip: skip,
                take: take,
                filter: filter,
                sortField:
                    sortedIndex === 0
                        ? PaginatedEmployeesSortableFields.FirstName
                        : sortedIndex === 1
                        ? PaginatedEmployeesSortableFields.LastName
                        : null,
                sortOrder:
                    sortedOrder === 'asc'
                        ? ColumnOrder.Asc
                        : sortedOrder === 'desc'
                        ? ColumnOrder.Desc
                        : null,
                branchId: branchId,
                orderProductionTypeId: orderProductionTypeId,
            },
        });
    }, [
        query,
        skip,
        take,
        filter,
        sortedOrder,
        sortedIndex,
        orderProductionTypeId,
        branchId,
    ]);

    const employees =
        data?.paginatedEmployees.docs ||
        previousData?.paginatedEmployees.docs ||
        [];

    const count =
        data && data?.paginatedEmployees.count >= 0
            ? data?.paginatedEmployees.count
            : previousData?.paginatedEmployees.count;

    const handleEditEmployee = useMemo(() => {
        return (
            employee: EmployeesPageQuery['paginatedEmployees']['docs'][number],
        ) => {
            setEmployeeId(employee.id);
            setUpsertOpen(true);
        };
    }, []);

    const handleDeleteEmployee = useMemo(() => {
        return (
            employee: EmployeesPageQuery['paginatedEmployees']['docs'][number],
        ) => {
            setEmployeeId(employee.id);
            setDeleteOpen(true);
        };
    }, []);

    return (
        <DPage>
            <DPageBody>
                <DBlock>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Empleados</Typography>
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <DTextField
                                label={'Filtro'}
                                helperText={'Nombre o apellido.'}
                                value={filter}
                                onChange={(value) => {
                                    setFilter(value || '');
                                }}
                                hideHelperTextBelowLg
                            />
                        </Box>
                        <DPaginator skip={skip} take={take} setSkip={setSkip} count={count} disableGutters />
                    </Box>
                    <CTableWrapper
                        onRowDoubleClick={(employee) => {
                            handleEditEmployee(employee);
                        }}
                        sortedIndex={sortedIndex}
                        setSortedIndex={setSortedIndex}
                        sortedOrder={sortedOrder}
                        setSortedOrder={setSortedOrder}
                        contextItems={[
                            {
                                title: shortLabel(COLUMN_LABELS.edit),
                                callback: (employee) => {
                                    handleEditEmployee(employee);
                                },
                            },
                            {
                                title: shortLabel(COLUMN_LABELS.delete),
                                disable: (employee) => {
                                    return !employee.is_deletable;
                                },
                                callback: (employee) => {
                                    handleDeleteEmployee(employee);
                                },
                            },
                        ]}
                        items={[
                            {
                                title: shortLabel(FIELD_LABELS.first_name),
                                cellGrow: 25,
                                sortable: true,
                                content: (employee) => {
                                    return employee.first_name;
                                },
                            },
                            {
                                title: shortLabel(FIELD_LABELS.last_name),
                                cellGrow: 25,
                                sortable: true,
                                content: (employee) => {
                                    return employee.last_name;
                                },
                            },
                            {
                                title: shortLabel(FIELD_LABELS.branch_id),
                                cellGrow: 20,
                                filter: (
                                    <Box sx={{ width: '100%', '& .MuiTextField-root': { minWidth: 0 } }}>
                                        <DIdAutocomplete
                                            id={branchId}
                                            onChange={setBranchId}
                                            itemText={'name'}
                                            label={'Sucursal'}
                                            options={data?.getBranches || []}
                                            disableGutters
                                        />
                                    </Box>
                                ),
                                content: (employee) => {
                                    return employee.branch?.name;
                                },
                            },
                            {
                                title: shortLabel(
                                    FIELD_LABELS.order_production_type_id,
                                ),
                                cellGrow: 20,
                                filter: (
                                    <Box sx={{ width: '100%', '& .MuiTextField-root': { minWidth: 0 } }}>
                                        <DIdAutocomplete
                                            id={orderProductionTypeId}
                                            onChange={setOrderProductionTypeId}
                                            itemText={'name'}
                                            label={'Tipo'}
                                            options={data?.getOrderProductionTypes || []}
                                            disableGutters
                                        />
                                    </Box>
                                ),
                                content: (employee) => {
                                    return employee.order_production_type?.name;
                                },
                            },
                            {
                                title: shortLabel(FIELD_LABELS.is_inactive),
                                cellGrow: 20,
                                content: (employee) => {
                                    return employee.is_inactive
                                        ? 'Baja'
                                        : 'Alta';
                                },
                            },
                            {
                                title: shortLabel(FIELD_LABELS.is_leader),
                                content: (item) => {
                                    return item?.is_leader ? (
                                        <Continued color={'success'} />
                                    ) : null;
                                },
                            },
                            {
                                title: shortLabel(FIELD_LABELS.updated_by_id),
                                cellGrow: 20,
                                content: (employee) => {
                                    return (
                                        employee.updated_by?.fullname || '—'
                                    );
                                },
                            },
                            {
                                title: '',
                                textAlign: 'center',
                                content: (employee) => {
                                    return (
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <IconButton
                                                size="small"
                                                sx={{ bgcolor: 'rgba(34,197,94,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                                onClick={() => {
                                                    handleEditEmployee(
                                                        employee,
                                                    );
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                sx={{ bgcolor: 'rgba(239,68,68,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                                disabled={
                                                    !employee.is_deletable
                                                }
                                                onClick={() => {
                                                    handleDeleteEmployee(
                                                        employee,
                                                    );
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    );
                                },
                            },
                        ]}
                        data={employees}
                        loading={loading}
                    />
                </DBlock>
            </DPageBody>
            <SUpsertEmployeeDialog
                open={upsertOpen}
                setOpen={setUpsertOpen}
                employeeId={employeeId}
            />
            <SDeleteEmployeeDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                employeeId={employeeId}
            />
        </DPage>
    );
}
